/**
 * Night lighting props: fighter torches + brazier flames.
 *
 * TORCH UX DECISION (agreed with the user): military fighters AUTO-carry
 * torches at night — no player micro, no inventory, no order. When
 * nightFactor crosses TORCH_NIGHT_THRESHOLD the integration layer
 * (main.ts / the fog-of-war coordinator) does two things:
 *   1. calls TorchField.sync, which shows a flame over each formation;
 *   2. registers a short-range "torch" vision viewer at each formation
 *      anchor for the fog-of-war system (so torches are the night-vision
 *      mechanic the user asked for, mirroring human night sight).
 * Workers and routing fighters also show flames (they're people, not
 * formations) — the gate is purely the light level, never the unit state.
 *
 * Braziers burn day and night; their point lights just burn brighter at
 * night (BRAZIER_LIGHT sidecar in data/prototype_overlay.ts).
 */
import * as THREE from "three";
import type { Combatant } from "../sim/combat";
import type { BuildingView } from "./buildings";
import { BRAZIER_ID, BRAZIER_LIGHT } from "../data/prototype_overlay";

/** Torches light when the night is more than half in. */
export const TORCH_NIGHT_THRESHOLD = 0.5;
/** Shared gate so fog-of-war uses the same rule as the visuals. */
export function torchesLit(nightFactor: number): boolean {
  return nightFactor > TORCH_NIGHT_THRESHOLD;
}

/** Flame height above a formation anchor (metres). */
const TORCH_HEIGHT = 3.4;
/** Flame quad size. */
const FLAME_W = 1.6;
const FLAME_H = 2.6;

let flameTexture: THREE.Texture | null = null;

/** Small radial-gradient flame sprite, generated once. */
function getFlameTexture(): THREE.Texture {
  if (flameTexture) return flameTexture;
  const c = document.createElement("canvas");
  c.width = 64;
  c.height = 64;
  const ctx = c.getContext("2d")!;
  const g = ctx.createRadialGradient(32, 40, 2, 32, 36, 30);
  g.addColorStop(0, "rgba(255,240,200,1)");
  g.addColorStop(0.25, "rgba(255,180,80,0.9)");
  g.addColorStop(0.55, "rgba(255,110,30,0.45)");
  g.addColorStop(1, "rgba(255,80,20,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 64, 64);
  flameTexture = new THREE.CanvasTexture(c);
  return flameTexture;
}

const tmpMat4 = new THREE.Matrix4();
const tmpPos = new THREE.Vector3();
const tmpQuat = new THREE.Quaternion();
const tmpScale = new THREE.Vector3();
const Y_AXIS = new THREE.Vector3(0, 1, 0);

/**
 * One flame per military formation, as crossed additive quads in a single
 * InstancedMesh (no per-instance billboarding cost). Hidden in daylight.
 */
export class TorchField {
  private mesh: THREE.InstancedMesh | null = null;
  private material: THREE.MeshBasicMaterial;
  private capacity = 0;

  constructor(private readonly scene: THREE.Scene) {
    this.material = new THREE.MeshBasicMaterial({
      map: getFlameTexture(),
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
  }

  private ensureCapacity(fighters: number): void {
    const need = fighters * 2; // two crossed quads per torch
    if (need <= this.capacity) return;
    if (this.mesh) {
      this.scene.remove(this.mesh);
      this.mesh.dispose();
    }
    const geo = new THREE.PlaneGeometry(FLAME_W, FLAME_H);
    this.mesh = new THREE.InstancedMesh(geo, this.material, Math.max(need, 16));
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 5;
    this.capacity = Math.max(need, 16);
    this.scene.add(this.mesh);
  }

  sync(
    fighters: Combatant[],
    nightFactor: number,
    groundY: (x: number, z: number) => number,
  ): void {
    this.ensureCapacity(fighters.length);
    const mesh = this.mesh!;
    const lit = torchesLit(nightFactor);
    mesh.visible = lit;
    if (!lit) return;
    // Smooth fade-in across the threshold rather than a hard pop.
    this.material.opacity = Math.min(1, (nightFactor - TORCH_NIGHT_THRESHOLD) * 4);
    const t = performance.now() / 1000;
    let idx = 0;
    for (let i = 0; i < fighters.length; i++) {
      const f = fighters[i];
      const { x, z } = f.view.anchor;
      // Flicker: fast small wobble + slow swell, per-torch phase.
      const flick =
        1 + 0.14 * Math.sin(t * 13 + i * 2.39) + 0.08 * Math.sin(t * 5.7 + i * 1.13);
      const s = Math.max(0.01, flick);
      const y = groundY(x, z) + TORCH_HEIGHT;
      for (let k = 0; k < 2; k++) {
        tmpPos.set(x, y, z);
        tmpQuat.setFromAxisAngle(Y_AXIS, (k * Math.PI) / 2 + t * 0.4);
        tmpScale.set(s, s, s);
        tmpMat4.compose(tmpPos, tmpQuat, tmpScale);
        mesh.setMatrixAt(idx++, tmpMat4);
      }
    }
    mesh.count = idx;
    mesh.instanceMatrix.needsUpdate = true;
  }

  dispose(): void {
    if (this.mesh) {
      this.scene.remove(this.mesh);
      this.mesh.dispose();
      this.mesh = null;
    }
    this.material.dispose();
  }
}

/** Point lights are expensive: cap the live pool. */
const BRAZIER_LIGHT_POOL = 12;
/** Ember marker size at the brazier crown. */
const EMBER_R = 0.55;

/**
 * Brazier presentation: a pooled set of orange point lights plus a small
 * emissive ember marker per active brazier. The base stone-box view comes
 * from createBuildingView (footprint-driven) — no per-id branch needed in
 * render/buildings.ts; the fire lives entirely here.
 */
export class BrazierLights {
  private readonly group = new THREE.Group();
  private readonly lights: THREE.PointLight[] = [];
  /** Ember marker groups keyed by building uid. */
  private readonly embers = new Map<number, THREE.Group>();
  private readonly emberMaterial: THREE.MeshBasicMaterial;
  private readonly glowMaterial: THREE.SpriteMaterial;

  constructor(private readonly scene: THREE.Scene) {
    this.group.name = "braziers";
    for (let i = 0; i < BRAZIER_LIGHT_POOL; i++) {
      const l = new THREE.PointLight(0xff9a3c, 0, 40, 2);
      l.visible = false;
      this.lights.push(l);
      this.group.add(l);
    }
    this.emberMaterial = new THREE.MeshBasicMaterial({ color: 0xffb347 });
    this.glowMaterial = new THREE.SpriteMaterial({
      map: getFlameTexture(),
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      opacity: 0.8,
    });
    scene.add(this.group);
  }

  private addEmber(uid: number): THREE.Group {
    const g = new THREE.Group();
    const ember = new THREE.Mesh(
      new THREE.SphereGeometry(EMBER_R, 10, 8),
      this.emberMaterial,
    );
    const glow = new THREE.Sprite(this.glowMaterial.clone());
    glow.scale.set(4.5, 4.5, 1);
    g.add(ember, glow);
    this.embers.set(uid, g);
    this.group.add(g);
    return g;
  }

  sync(
    buildingViews: BuildingView[],
    nightFactor: number,
    groundY: (x: number, z: number) => number,
  ): void {
    const braziers = buildingViews.filter(
      (v) => v.building.def.id === BRAZIER_ID && v.building.state === "active",
    );
    const live = new Set(braziers.map((v) => v.building.uid));
    // Drop markers for braziers that were destroyed/removed.
    for (const [uid, g] of this.embers) {
      if (!live.has(uid)) {
        this.group.remove(g);
        this.embers.delete(uid);
      }
    }
    const t = performance.now() / 1000;
    for (let i = 0; i < braziers.length; i++) {
      const v = braziers[i];
      const b = v.building;
      const gy = groundY(b.x, b.z);
      const marker = this.embers.get(b.uid) ?? this.addEmber(b.uid);
      marker.position.set(b.x, gy + 2.9, b.z);
      const flick = 1 + 0.12 * Math.sin(t * 11 + b.uid * 1.7) + 0.06 * Math.sin(t * 23 + b.uid * 3.1);
      const emberScale = (0.8 + 0.4 * nightFactor) * flick;
      marker.scale.setScalar(emberScale);
      const glow = marker.children[1] as THREE.Sprite;
      (glow.material as THREE.SpriteMaterial).opacity = 0.35 + 0.55 * nightFactor;
      // Pooled point lights: first N braziers get one.
      if (i < this.lights.length) {
        const l = this.lights[i];
        l.visible = true;
        l.position.set(b.x, gy + 2.2, b.z);
        l.intensity =
          (BRAZIER_LIGHT.day + (BRAZIER_LIGHT.night - BRAZIER_LIGHT.day) * nightFactor) * flick;
      }
    }
    for (let i = braziers.length; i < this.lights.length; i++) {
      this.lights[i].visible = false;
    }
  }

  dispose(): void {
    this.scene.remove(this.group);
    this.emberMaterial.dispose();
    this.glowMaterial.dispose();
  }
}
